import {
  memo,
  useRef,
  useMemo,
  RefObject,
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  createRef,
} from 'react';
import { Box, useLayout, useStyle, ViewStyle } from '@betty/moonlight';
import { Game } from '../../../state/shared/games.types';
import { selectAvailableGames } from '../../../state';
import { GamesRandomizerColumnExposedProps } from './GamesRandomizerColumn/GamesRandomizerColumn.types';
import {
  GamesRandomizerColumnsProps,
  GamesRandomizerColumnsExposedProps,
} from './GamesRandomizerColumns.types';
import {
  IMAGES_GAP_TOKEN,
  IMAGES_CONTAINER_HEIGHT,
} from '../GamesRandomizer.content';
import {
  getColumns,
  getColumnHeight,
  getColumnsCount,
  getTargetGameOffset,
  getItemSize,
} from '../GamesRandomizer.utils';
import GamesRandomizerColumn from './GamesRandomizerColumn/GamesRandomizerColumn';
import { useSelector } from '@betty/toolkit/state';

function GamesRandomizerColumns(
  { targetGameId }: GamesRandomizerColumnsProps,
  ref: ForwardedRef<GamesRandomizerColumnsExposedProps>,
) {
  const [layout, onLayout] = useLayout();
  const { gap } = useStyle<ViewStyle>({ gap: IMAGES_GAP_TOKEN });
  const games = useSelector(selectAvailableGames);
  const columnsCount = getColumnsCount(+gap!, layout.width);
  const columns = useMemo(
    () => getColumns(games, columnsCount),
    [games, columnsCount],
  );
  const columnRefs = useRef<RefObject<GamesRandomizerColumnExposedProps>[]>([]);
  const itemSize = getItemSize(+gap!, columnsCount, layout.width);

  const onChoosingStart = useCallback(
    (randomId: Game['id'], onGameChoosed: () => void) => {
      const targetOffset = getTargetGameOffset(
        +gap!,
        columns,
        randomId,
        itemSize,
      );

      if (!targetOffset) {
        return;
      }

      columnRefs.current.forEach((column) =>
        column.current?.onChoosingStart(randomId, targetOffset, onGameChoosed),
      );
    },
    [gap, columns, itemSize],
  );

  const onChoosingStop = useCallback(() => {
    columnRefs.current.forEach((column) => column.current?.onChoosingStop());
  }, []);

  useEffect(() => {
    columnRefs.current = columnRefs.current.slice(0, columnsCount);
  }, [columnsCount]);

  useImperativeHandle(
    ref,
    () => ({
      onChoosingStart,
      onChoosingStop,
    }),
    [onChoosingStart, onChoosingStop],
  );

  return (
    <Box
      key={columnsCount}
      flexDirection="row"
      justifyContent="center"
      overflow="hidden"
      height={IMAGES_CONTAINER_HEIGHT}
      gap={IMAGES_GAP_TOKEN}
      onLayout={onLayout}
    >
      {columns.map((gameIds, index) => {
        if (!columnRefs.current[index]) {
          columnRefs.current[index] = createRef();
        }

        return (
          <GamesRandomizerColumn
            key={index}
            ref={columnRefs.current[index]}
            gameIds={gameIds}
            gamesHeight={getColumnHeight(+gap!, gameIds, itemSize)}
            targetGameId={targetGameId}
            itemSize={itemSize}
            isEven={index % 2 === 0}
          />
        );
      })}
    </Box>
  );
}

export default memo(forwardRef(GamesRandomizerColumns));
